import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ProductPrice } from '../../components/ProductPrice';
import { Fit } from '../../enums/Fit.enum';
import { ProductHandle } from '../../enums/ProductHandle.enum';
import { SubContext } from '../../enums/SubContext.enum';
import { Direction, useProductAssets } from '../../hooks/useProductAssets';
import { useProductDiscountPercent } from '../../hooks/useProductDiscountPercent';
import { ViewVariant } from '../../models/ShopifyVariant.model';
import { ViewProduct } from '../../models/ViewProduct.model';
import ColorSwatch from '../../product_detail/components/ColorSwatch';
import { blankImage } from '../../product_detail/components/ImageLoader';
import { LazyImageLoader } from '../../product_detail/components/LazyImageLoader';
import { QuickViewLink } from '../../product_detail/components/QuickViewLink';
import { collectionSelectedVariantFamily, pageContextState } from '../../recoil/atoms';
import { isMobileState } from '../../recoil/selectors';
import { Link } from '../../root/components/Link';
import { sizeImage, SIZE_TO_FILTER_BY } from '../../util';
import styles from './CollectionCard.module.scss';

interface Props {
  product: ViewProduct;
  setHoverProductHandle: (productHandle: ProductHandle | undefined) => void;
  clearHoverProductHandleDebounce: () => void;
  selectionKey: string;
}

export const CollectionCard: React.FC<Props> = ({ product, setHoverProductHandle, clearHoverProductHandleDebounce, selectionKey }) => {
  const isMobile = useRecoilValue(isMobileState);
  const photos = useProductAssets(product);
  const [selectedVariant, setVariant] = useRecoilState(collectionSelectedVariantFamily(product.handle));
  const displayVariants = product.variants.filter((v) => v.size === SIZE_TO_FILTER_BY && (!v.fit || v.fit === Fit.Regular));
  const pageContext = useRecoilValue(pageContextState);

  const filteredMannequinPhotos = photos.mannequinPhotos.filter(({ color, direction }) => color === selectedVariant.color && direction === Direction.Front);
  const filteredModelPhotos = photos.modelPhotos.filter(({ color }) => color === selectedVariant.color);
  const mainPhoto = filteredMannequinPhotos[0] ?? filteredModelPhotos[0] ?? { src: blankImage };
  const mainPhotoSrc = sizeImage(mainPhoto.src, '350x443');
  const hoverPhoto = filteredModelPhotos[0] ?? filteredMannequinPhotos[0] ?? { src: blankImage };
  const hoverPhotoSrc = sizeImage(hoverPhoto.src, '350x443');
  const [activeSrc, setActiveSrc] = useState<string>(mainPhotoSrc);
  const [isHovering, setIsHovering] = useState<boolean>(false);

  const percentOff = useProductDiscountPercent(product, selectedVariant, selectedVariant.color);
  const showSaleBadge = percentOff > 0;
  const saleBadgeContents = `${percentOff}% off`;

  // Update the image when the color changes
  useEffect(() => {
    if (isHovering && !isMobile) {
      setActiveSrc(hoverPhotoSrc);
    } else {
      setActiveSrc(mainPhotoSrc);
    }
  }, [hoverPhotoSrc, isHovering, isMobile, mainPhotoSrc, selectedVariant]);

  const setColor = (variant: ViewVariant) => {
    setVariant(variant);
  };

  useEffect(() => {
    // This is a hack, and I really don't like it
    // But it is required unless we refactor
    // Nav categories and side-effects of the useNavCategories hook
    if (!product.colors.includes(selectedVariant.color)) {
      setVariant(product.variants[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={styles.collectionCard}
      onMouseEnter={(): void => {
        if (!isMobile) {
          setHoverProductHandle(product.handle);
          setIsHovering(true);
        }
      }}
      onBlur={() => void 0}
      onFocus={() => void 0}
      onMouseLeave={(): void => {
        if (!isMobile) {
          clearHoverProductHandleDebounce();
          setIsHovering(false);
        }
      }}
    >
      {showSaleBadge && <span className={styles.saleBadge}>{saleBadgeContents}</span>}
      <Link
        dataTrackLabel="Collections product image"
        dataTrackLocation={pageContext}
        href={`/products/${product.handle}?color=${selectedVariant.color}`}
        buttonType="ghost"
      >
        <div style={{ display: 'flex', position: 'relative' }}>
          <div style={{ marginTop: '127%' }}></div> {/* this holds the aspect ratio of the image */}
          <LazyImageLoader
            src={activeSrc}
            alt={`${product.title} shown in ${selectedVariant.color}`}
            style={
              isMobile || isHovering
                ? { width: '100%', position: 'absolute', top: 0, left: 0 }
                : { width: '80%', position: 'absolute', top: '10%', left: '10%' }
            }
          />
        </div>
      </Link>
      <div className={styles.quickViewWrapper}>
        <QuickViewLink
          size="small"
          product={product}
          selectionKey={selectionKey}
          initialColor={selectedVariant.color}
          subContext={SubContext.CollectionsSet}
          dataTrackLabel="Quick view"
          dataTrackLocation={pageContext}
          text={'Quick view'}
        />
      </div>
      <div className={styles.productInfo}>
        <div className={styles.title}>{product.title}</div>
        <ProductPrice
          product={product}
          variant={selectedVariant}
          style={
            isMobile
              ? { fontWeight: '400', textAlign: 'center', lineHeight: '20px', fontSize: '12px' }
              : { fontWeight: '400', textAlign: 'center', lineHeight: '28px', fontSize: '16px' }
          }
        />
      </div>
      <div className={styles.colorContainer}>
        {displayVariants.map((variant) => (
          <ColorSwatch
            key={variant.id}
            color={variant.color}
            active={selectedVariant.color === variant.color}
            activate={() => setColor(variant)}
            showSmall={isMobile}
          />
        ))}
      </div>
    </div>
  );
};

export default CollectionCard;
